import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import {
  TrendingDown,
  Waves,
  GaugeCircle,
  Workflow,
  UserCog,
  FileSpreadsheet,
  Coins,
  RotateCcw,
} from "lucide-react";
import Reveal from "./Reveal";

/**
 * Each pain point is paired with an icon that visually reflects the problem.
 * Icons follow the same order as `problems.items` in the translations file.
 */
const PAIN_ICONS = [
  TrendingDown,       // Faturamento cresce, lucro não
  Waves,              // Fluxo de caixa desorganizado
  GaugeCircle,        // Falta de indicadores
  Workflow,           // Processos informais
  UserCog,            // Dependência do proprietário
  FileSpreadsheet,    // Planilhas em excesso
  Coins,              // Pouco controle sobre custos
  RotateCcw,          // Muito retrabalho
];

export default function Problems() {
  const { t } = useLang();

  return (
    <section
      id="problems"
      data-testid="problems-section"
      className="py-24 md:py-32"
      style={{ background: "var(--sap-gray-50)" }}
    >
      <div className="sap-container">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="sap-divider-gold" />
            <span className="sap-overline">{t.problems.overline}</span>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <h2
              className="font-serif leading-[1.1] tracking-tight"
              style={{
                fontSize: "clamp(1.9rem, 3.6vw, 3.2rem)",
                color: "var(--sap-navy)",
                fontWeight: 400,
              }}
            >
              {t.problems.title}
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={120}>
            <p className="text-base md:text-lg leading-relaxed text-neutral-600">
              {t.problems.subtitle}
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
          {t.problems.items.map((item, i) => {
            const Icon = PAIN_ICONS[i] || Workflow;
            return (
              <Reveal key={i} delay={i * 60}>
                <div
                  data-testid={`problem-item-${i}`}
                  className="group relative bg-white p-8 h-full transition-colors duration-300 hover:bg-[color:var(--sap-navy)] flex flex-col"
                >
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 mb-6 border transition-colors"
                    style={{
                      borderColor: "#e7e7e7",
                    }}
                  >
                    <Icon
                      size={24}
                      strokeWidth={1.4}
                      className="text-[color:var(--sap-navy)] group-hover:text-[color:var(--sap-gold)] transition-colors"
                    />
                  </div>

                  <p className="text-[15px] leading-relaxed text-[color:var(--sap-navy)] group-hover:text-white transition-colors flex-1">
                    {item}
                  </p>

                  <div className="mt-8 flex items-center justify-between">
                    <div
                      className="text-[11px] tracking-[0.28em] uppercase text-neutral-400 group-hover:text-[color:var(--sap-gold)] transition-colors"
                    >
                      {String(i + 1).padStart(2, "0")} / 08
                    </div>
                    <span
                      className="block w-8 h-px transition-all group-hover:w-14"
                      style={{ background: "var(--sap-gold)" }}
                    />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
